import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { MenuItem } from "../types/navigation";

export const useSubmenu = () => {
  const navigate = useNavigate();
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [isClicked, setIsClicked] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRefs = useRef<{ [key: string]: HTMLAnchorElement | null }>({});

  const calculateSubmenuPosition = (menuKey: string) => {
    const menuElement = menuRefs.current[menuKey];
    const containerElement = containerRef.current;

    if (menuElement && containerElement) {
      const menuRect = menuElement.getBoundingClientRect();
      const containerRect = containerElement.getBoundingClientRect();

      const menuCenter = menuRect.left + menuRect.width / 2 - containerRect.left;
      const submenuWidth = containerRect.width * 0.3;
      const leftPosition = menuCenter - submenuWidth / 2;
      const maxLeft = containerRect.width * 0.9 - submenuWidth;
      const minLeft = containerRect.width * 0.05;

      return Math.max(minLeft, Math.min(maxLeft, leftPosition));
    }

    return 0;
  };

  const go = (menuItem: MenuItem) => {
    if (menuItem.onClick) {
      menuItem.onClick();
    }
    if (menuItem.href) {
      navigate(menuItem.href);
    }
  };

  const handleMenuClick = (menuItem: MenuItem) => {
    if (menuItem.submenu && menuItem.submenu.length > 0) {
      if (activeSubmenu === menuItem.key && isClicked) {
        setActiveSubmenu(null);
        setIsClicked(false);
      } else {
        const position = calculateSubmenuPosition(menuItem.key);
        setSubmenuPosition(position);
        setActiveSubmenu(menuItem.key);
        setIsClicked(true);
      }
    } else {
      setActiveSubmenu(null);
      setIsClicked(false);
      go(menuItem);
    }
  };

  const handleMenuHover = (menuItem: MenuItem) => {
    if (menuItem.submenu && menuItem.submenu.length > 0) {
      if (!isClicked) {
        const position = calculateSubmenuPosition(menuItem.key);
        setSubmenuPosition(position);
        setActiveSubmenu(menuItem.key);
      }
    }
  };

  const handleMenuLeave = () => {
    if (!isClicked) {
      setActiveSubmenu(null);
    }
  };

  const handleContainerLeave = () => {
    if (!isClicked) {
      setActiveSubmenu(null);
    }
  };

  const closeSubmenu = () => {
    setActiveSubmenu(null);
    setIsClicked(false);
  };

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isNavElement =
        target.closest("[data-navbar-element]") ||
        target.closest(".navbar-container") ||
        target.closest(".submenu-container");

      if (!isNavElement && activeSubmenu) {
        closeSubmenu();
      }
    };

    if (activeSubmenu) {
      document.addEventListener("mousedown", handleDocumentClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, [activeSubmenu]);

  useEffect(() => {
    const handleResize = () => {
      if (activeSubmenu) {
        const position = calculateSubmenuPosition(activeSubmenu);
        setSubmenuPosition(position);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeSubmenu]);

  return {
    activeSubmenu,
    isClicked,
    submenuPosition,
    containerRef,
    menuRefs,
    handleMenuClick,
    handleMenuHover,
    handleMenuLeave,
    handleContainerLeave,
    closeSubmenu,
  };
};
